import { WarningIcon } from "@phosphor-icons/react/dist/ssr/Warning";
import { motion } from "motion/react";
import { useMemo, useReducer, useRef } from "react";

import { logError, useTheme } from "@up/data";
import { Form } from "@up/form";
import { Check, MOTION, Spinner } from "@up/ui";

import {
  StudentAge,
  StudentCountry,
  StudentDOB,
  StudentFirstName,
  StudentLastName,
  StudentZip,
} from "./fields";
import { saveStudent } from "../network";

import type { StudentFormObject } from "../types";
import type { TCart } from "@up/data";

type Status = "idle" | "saving" | "saved" | "error";

type StatusType = {
  status: Status;
  errorMessage: string;
};

type ActionType =
  | { type: "idle" }
  | { type: "saving" }
  | { type: "saved" }
  | { type: "error"; payload: string };

type Props = {
  close: () => void;
  student: TCart["student"];
  saveSuccess: (cart: TCart) => void;
  dobRequired: boolean;
};

const formatAge = function (age: string | number): string {
  if (typeof age === "string") {
    return age;
  } else if (age >= 18) {
    return "18";
  }
  return age.toString();
};

export default function StudentInformationForm({
  close,
  student,
  dobRequired,
  saveSuccess,
}: Props) {
  const theme = useTheme();
  const savedCartRef = useRef<TCart | null>(null);

  const [save, saveDispatch] = useReducer(
    function (
      state: { status: Status; errorMessage: string },
      action: ActionType,
    ): StatusType {
      const nextState = { ...state };

      switch (action.type) {
        case "saving":
          return {
            ...nextState,
            status: "saving",
          };
        case "saved":
          return {
            ...nextState,
            status: "saved",
          };
        case "error":
          return {
            ...nextState,
            status: "error",
            errorMessage: action.payload,
          };
        default:
          return nextState;
      }
    },
    {
      status: "idle",
      errorMessage: "",
    },
  );

  const onSubmit = async function (data: Record<string, unknown>) {
    const s = data as StudentFormObject;
    const { country, region, zipCode, dob, age, ...rest } = s;

    const updatedStudent: TCart["student"] = {
      contactId: student.contactId,
      dob,
      age,
      address: {
        country,
        region,
        zipCode,
      },
      ...rest,
    };

    saveDispatch({ type: "saving" });
    try {
      const cart = await saveStudent(updatedStudent);
      savedCartRef.current = cart;
      saveDispatch({ type: "saved" });
    } catch (err) {
      saveDispatch({ type: "error", payload: "save error" });
      logError({ action: "STUDENT_INFO_SAVE", error: err });
    }
  };

  const userData = useMemo(() => {
    const age = theme.noGuardian
      ? "18"
      : student?.age
        ? formatAge(student.age)
        : "";
    return {
      firstName: student?.firstName ?? "",
      lastName: student?.lastName ?? "",
      email: student?.email ?? "",
      age: age,
      dob: student?.dob ?? "",
      country: student?.address?.country ?? "",
      region: student?.address?.region ?? "",
      zipCode: student?.address?.zipCode ?? "",
      touAccept: true,
    };
  }, [student, theme.noGuardian]);

  const IS_SAVING = save.status === "saving";
  const DID_SAVE = save.status === "saved";
  const DID_ERROR = save.status === "error";

  return (
    <motion.div className="overflow-hidden" {...MOTION["expand"].props}>
      <div className="pt-2" data-testid="student-form">
        <Form onSubmit={onSubmit} userData={userData} validateOnMount={true}>
          {({ control, errors, formValues, register, trigger }) => {
            return (
              <>
                <div
                  className={`gap-4 md:grid ${theme.noGuardian ? "md:grid-cols-2" : "md:grid-cols-3"}`}
                >
                  <StudentFirstName
                    formErrors={errors}
                    formValues={formValues}
                    register={register}
                  />
                  <StudentLastName
                    formErrors={errors}
                    formValues={formValues}
                    register={register}
                  />
                  {dobRequired ? (
                    <StudentDOB
                      formErrors={errors}
                      formValues={formValues}
                      register={register}
                    />
                  ) : theme.noGuardian ? (
                    <input type="hidden" value="18" {...register("age")} />
                  ) : (
                    <StudentAge
                      control={control}
                      formErrors={errors}
                      formValues={formValues}
                      register={register}
                    />
                  )}
                </div>

                <div className="gap-4 md:grid md:grid-cols-3">
                  <div className="col-span-2">
                    <StudentCountry
                      formErrors={errors}
                      formValues={formValues}
                      register={register}
                    />
                  </div>
                </div>

                <div className="gap-4 md:grid md:grid-cols-3">
                  <StudentZip
                    formErrors={errors}
                    formValues={formValues}
                    register={register}
                  />
                </div>

                {DID_ERROR ? (
                  <div className="mb-3 flex items-center font-bold text-red-700">
                    <WarningIcon className="mr-2" size={24} weight="fill" />
                    We had trouble saving your changes. Please try again.
                  </div>
                ) : null}

                <div className="flex items-center">
                  <input
                    className={`${theme.buttonPrimaryClass} mr-2 ${
                      IS_SAVING ? "opacity-75" : ""
                    }`}
                    disabled={IS_SAVING}
                    type="submit"
                    value="Save Changes"
                  />
                  <input
                    className={`${theme.buttonSecondaryClass} ${
                      IS_SAVING ? "opacity-75" : ""
                    }`}
                    disabled={IS_SAVING}
                    onClick={close}
                    type="button"
                    value="Cancel"
                  />
                  {IS_SAVING ? <Spinner className="ml-4" /> : null}
                  {DID_SAVE ? (
                    <div className="ml-2">
                      <Check
                        animationCallback={() => {
                          if (savedCartRef.current) {
                            saveSuccess(savedCartRef.current);
                          }
                        }}
                        size={44}
                      />
                    </div>
                  ) : null}
                </div>
              </>
            );
          }}
        </Form>
      </div>
    </motion.div>
  );
}
